
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type CurrencyCode = 'USD' | 'EUR' | 'GBP' | 'DKK' | 'SEK';

export interface CurrencyInfo {
  code: CurrencyCode;
  symbol: string;
  name: string;
}

export const CURRENCIES: Record<CurrencyCode, CurrencyInfo> = {
  USD: { code: 'USD', symbol: '$', name: 'US Dollar' },
  EUR: { code: 'EUR', symbol: '€', name: 'Euro' },
  GBP: { code: 'GBP', symbol: '£', name: 'British Pound' },
  DKK: { code: 'DKK', symbol: 'kr', name: 'Danish Krone' },
  SEK: { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
};

export const DEFAULT_CURRENCY: CurrencyCode = 'EUR';

interface CurrencyStore {
  currency: CurrencyCode;
  setCurrency: (currency: CurrencyCode) => void;
  // Add a new action that will only be called from the settings page
  saveUserCurrency: (currency: CurrencyCode) => void;
}

export const useCurrencyStore = create<CurrencyStore>()(
  persist(
    (set) => ({
      currency: DEFAULT_CURRENCY,
      setCurrency: (currency: CurrencyCode) => set({ currency }),
      saveUserCurrency: (currency: CurrencyCode) => set({ currency }),
    }),
    {
      name: 'currency-settings',
    }
  )
);

export const formatCurrency = (
  amount: number, 
  currencyCode?: CurrencyCode, 
  compact: boolean = false,
  showDecimals: boolean = true
): string => {
  const code = currencyCode || useCurrencyStore.getState().currency;
  
  const options: Intl.NumberFormatOptions = { 
    style: 'currency', 
    currency: code
  };
  
  // Add decimal options
  if (!showDecimals) {
    options.minimumFractionDigits = 0;
    options.maximumFractionDigits = 0;
  }
  
  if (compact) {
    // For YAxis labels, use a more compact format
    if (amount >= 1000000) {
      return CURRENCIES[code].symbol + (Math.round(amount / 100000) / 10) + 'M';
    }
    if (amount >= 1000) {
      return CURRENCIES[code].symbol + Math.round(amount / 1000) + 'k';
    }
    return CURRENCIES[code].symbol + amount;
  }
  
  return new Intl.NumberFormat('en-US', options).format(amount);
};

export const formatPercentage = (value: number): string => {
  return `${Math.round(value)}%`;
};

export const getGrowthIndicator = (current: number, previous: number): {
  growth: number;
  isPositive: boolean;
} => {
  if (previous === 0) return { growth: 0, isPositive: true };
  
  const growthRate = ((current - previous) / previous) * 100;
  return {
    growth: Math.abs(Math.round(growthRate)),
    isPositive: growthRate >= 0
  };
};
